<script module lang="ts">
  import type { ChatChannel } from '@arena/shared';

  /** Outlive each Chat, so going between the hub and a match keeps them. */
  let picked = $state<ChatChannel>('say');
  /** Who plain lines go to instead of the channel, after /w <name>. */
  let whisperTo = $state<string | null>(null);
</script>

<script lang="ts">
  import {
    CHAT_CHANNELS,
    type ChatLine,
    type ClientMessage,
    type Emote,
  } from '@arena/shared';
  import { click } from '../game/Sfx';
  import { app, send } from '../lib/net.svelte';
  import { settings } from '../lib/settings.svelte';

  let {
    channels,
    oninvite,
    hub = false,
    onname,
  }: {
    channels: readonly ChatChannel[];
    /** Enables /invite; absent in a match. */
    oninvite?: (name: string) => void;
    /** Enables /sit, /kneel, /nod, /no and /join; the hub only. */
    hub?: boolean;
    /** Makes other players' names clickable. */
    onname?: (name: string, event: MouseEvent) => void;
  } = $props();
  let chatText = $state('');
  /** The last channel picked, or this chat's first while that one isn't offered here. */
  const channel = $derived(channels.includes(picked) ? picked : channels[0]);

  const CHANNEL_COMMANDS: Record<string, ChatChannel> = {
    s: 'say',
    say: 'say',
    y: 'yell',
    yell: 'yell',
    p: 'party',
    party: 'party',
  };
  const EMOTE_COMMANDS: Record<string, Emote> = {
    sit: 'sit',
    kneel: 'kneel',
    nod: 'yes',
    yes: 'yes',
    no: 'no',
  };
  const CHANNEL_NAMES: Record<ChatChannel, string> = {
    say: 'Say',
    yell: 'Yell',
    party: 'Party',
  };
  /** Who a line is from, or who our own whisper went to. */
  const speaker = (line: ChatLine) => line.to ?? line.from;
  /** Who /r replies to. */
  const lastWhisperer = () =>
    app.chat.findLast((l) => l.channel === 'whisper' && !l.to)?.from;
  type Command = [usage: string, what?: string];
  /** What the Commands popover lists; keep in step with `sendChat`. */
  const commands = $derived<Command[]>([
    ...CHAT_CHANNELS.filter((c) => c === 'party' || channels.includes(c)).map(
      (c): Command => [`/${c[0]}`, CHANNEL_NAMES[c]],
    ),
    ['/w <name>', 'Whisper'],
    ['/r', 'Reply to last whisper'],
    ...(oninvite
      ? ([['/invite <name>', 'Invite to party']] satisfies Command[])
      : []),
    ...(hub
      ? ([
          ['/sit'],
          ['/kneel'],
          ['/nod'],
          ['/no'],
          ['/join hub-<number>', 'Change hub'],
        ] satisfies Command[])
      : []),
    ['/ignore <name>', 'Hide their chat'],
    ['/unignore <name>'],
    ['/report <name> <reason>', 'Report a player'],
  ]);
  let showCommands = $state(false);
  let commandsEl: HTMLDivElement | undefined = $state();
  let chatLog: HTMLDivElement | undefined = $state();
  let chatInput: HTMLInputElement | undefined = $state();

  $effect(() => {
    void app.chat.length;
    chatLog?.scrollTo({ top: chatLog.scrollHeight });
  });

  const channelFor = (command: string) => {
    const next = CHANNEL_COMMANDS[command.toLowerCase()];
    return next && channels.includes(next) ? next : undefined;
  };

  /** Typing "/p " (or /s, /y, /w <name> ) switches channel on the spot, like WoW. */
  function switchChannel() {
    const to = chatText.match(
      /^\/(?:w|whisper)\s+(?:"([^"]+)"|([^"\s]\S*))\s/i,
    );
    if (to) {
      whisperTo = to[1] ?? to[2];
      chatText = chatText.slice(to[0].length);
      return;
    }
    const replyTo = /^\/(?:r|reply)\s/i.test(chatText) && lastWhisperer();
    if (replyTo) {
      whisperTo = replyTo;
      chatText = chatText.replace(/^\/\w+\s/, '');
      return;
    }
    const command = chatText.match(/^\/(\w+)\s/);
    const next = command && channelFor(command[1]);
    if (!next) return;
    picked = next;
    whisperTo = null;
    chatText = chatText.slice(command[0].length);
  }

  function sendChat(event: SubmitEvent) {
    event.preventDefault();
    const text = chatText.trim();
    chatText = '';
    chatInput?.blur();
    const command = text.match(/^\/(\w+)\s*(.*)$/);
    if (!command) {
      if (text && whisperTo) send({ t: 'whisper', to: whisperTo, text });
      else if (text) send({ t: 'chat', channel, text });
      return;
    }
    const [, name, rest] = command;
    const next = channelFor(name);
    if (next) {
      picked = next;
      whisperTo = null;
      if (rest) send({ t: 'chat', channel: next, text: rest });
    } else if (CHANNEL_COMMANDS[name.toLowerCase()] === 'party') {
      system('You are not in a party.');
    } else if (['w', 'whisper'].includes(name.toLowerCase())) {
      const to = rest.match(/^(?:"([^"]+)"|([^"\s]\S*))\s*(.*)$/);
      if (!to) return system('Whisper with /w <name>.');
      whisperTo = to[1] ?? to[2];
      if (to[3]) send({ t: 'whisper', to: whisperTo, text: to[3] });
    } else if (['r', 'reply'].includes(name.toLowerCase())) {
      const replyTo = lastWhisperer();
      if (!replyTo) return system('No one has whispered you.');
      whisperTo = replyTo;
      if (rest) send({ t: 'whisper', to: whisperTo, text: rest });
    } else if (oninvite && ['invite', 'inv'].includes(name.toLowerCase())) {
      oninvite(rest);
    } else if (hub && EMOTE_COMMANDS[name.toLowerCase()]) {
      send({ t: 'emote', emote: EMOTE_COMMANDS[name.toLowerCase()] });
    } else if (hub && name.toLowerCase() === 'join') {
      const n = Number(rest.trim().match(/^(?:hub-?)?(\d{1,4})$/i)?.[1]);
      if (n > 0) send({ t: 'joinHub', hub: n });
      else system('Join a hub with /join hub-<number>.');
    } else if (['ignore', 'unignore'].includes(name.toLowerCase())) {
      ignore(name.toLowerCase() === 'ignore', rest.trim());
    } else if (name.toLowerCase() === 'report') {
      const [, who, reason] = rest.match(/^(\S+)\s+(.+)$/) ?? [];
      if (who) send({ t: 'report', name: who, reason });
      else system('Report with /report <name> <reason>.');
    } else if (MOD_COMMANDS.includes(name.toLowerCase() as ModAction)) {
      moderate(name.toLowerCase() as ModAction, rest.trim());
    } else {
      system(
        `Unknown command /${name}. Try ${commands.map(([usage]) => usage).join(', ')}.`,
      );
    }
  }

  function ignore(on: boolean, name: string) {
    if (!name)
      return system(
        settings.ignored.length
          ? `You are ignoring ${settings.ignored.join(', ')}.`
          : 'You are ignoring no one. Ignore a player with /ignore <name>.',
      );
    const key = name.toLowerCase();
    if (on && key === app.user?.name.toLowerCase())
      return system("You can't ignore yourself.");
    settings.ignored = on
      ? [...new Set([...settings.ignored, key])]
      : settings.ignored.filter((n) => n !== key);
    system(
      on
        ? `You are now ignoring ${name}.`
        : `You are no longer ignoring ${name}.`,
    );
  }

  type ModAction = Extract<ClientMessage, { t: 'moderate' }>['action'];
  const MOD_COMMANDS: ModAction[] = ['mute', 'unmute', 'kick', 'ban', 'unban'];
  const MOD_USAGE: Record<ModAction, string> = {
    mute: '/mute <name> <minutes> [reason]',
    unmute: '/unmute <name>',
    kick: '/kick <name>',
    ban: '/ban <name> <days, 0 for good> [reason]',
    unban: '/unban <name>',
  };

  /** Admin commands; the lobby refuses anyone else. */
  function moderate(action: ModAction, rest: string) {
    const [who, ...words] = rest.split(/\s+/).filter(Boolean);
    const timed = action === 'mute' || action === 'ban';
    const duration = timed ? Number(words.shift()) : undefined;
    if (!who || (timed && !(Number.isInteger(duration) && duration! >= 0)))
      return system(`Use ${MOD_USAGE[action]}.`);
    const reason = words.join(' ') || undefined;
    send({ t: 'moderate', action, name: who, duration, reason });
  }

  function system(text: string) {
    app.chat = [...app.chat, { channel: 'system', text, at: Date.now() }];
  }

  /** Switches the chat box to whispering `name`. */
  export function whisper(name: string) {
    whisperTo = name;
    chatInput?.focus();
  }

  function clickName(event: MouseEvent, name: string) {
    event.preventDefault();
    onname?.(name, event);
  }

  function onKey(event: KeyboardEvent) {
    if (event.target instanceof HTMLInputElement) {
      if (event.key === 'Escape') event.target.blur();
      return;
    }
    if (event.key !== 'Enter' && event.key !== '/') return;
    event.preventDefault();
    if (event.key === '/') chatText = '/';
    chatInput?.focus();
  }
</script>

<svelte:window
  onkeydown={onKey}
  onpointerdown={(e) => {
    if (!commandsEl?.contains(e.target as Node)) showCommands = false;
  }}
  onkeydowncapture={(e) => {
    if (e.key !== 'Escape' || !showCommands) return;
    showCommands = false;
    click();
    e.stopPropagation();
  }}
/>

<section class="panel chat">
  <div class="commands" bind:this={commandsEl}>
    {#if showCommands}
      <div class="panel list">
        {#each commands as [usage, what] (usage)}
          <code>{usage}</code><span>{what ?? ''}</span>
        {/each}
      </div>
    {/if}
    <button onclick={() => (showCommands = !showCommands)}>/ Commands</button>
  </div>
  {#if hub && app.hub}<span class="room">hub-{app.hub}</span>{/if}
  <div class="log" bind:this={chatLog}>
    {#each app.chat as line, i (i)}
      {@const name = speaker(line)}
      <div class={line.channel}>
        {line.channel === 'party' ? '[Party] ' : ''}{line.to
          ? 'To '
          : ''}{#if name && onname && (line.to || line.fromId !== app.user?.id)}<button
            class="name"
            onclick={(e) => clickName(e, name)}
            oncontextmenu={(e) => clickName(e, name)}>[{name}]</button
          >{:else if name}[{name}]{/if}{line.channel === 'whisper' && !line.to
          ? ' whispers'
          : ''}{name ? ': ' : ''}{line.text}
      </div>
    {/each}
  </div>
  <form onsubmit={sendChat}>
    {#if whisperTo}<span class="whisper">To {whisperTo}:</span>{/if}
    <input
      bind:this={chatInput}
      bind:value={chatText}
      class={whisperTo ? 'whisper' : channel}
      oninput={switchChannel}
      maxlength="200"
      placeholder="Press enter to chat"
    />
  </form>
</section>

<style>
  .chat {
    position: absolute;
    bottom: 112px;
    left: 12px;
    width: 380px;
    height: 220px;
    display: flex;
    flex-direction: column;
    background: rgba(10, 11, 16, 0.6);
    pointer-events: auto;
  }
  .commands {
    position: absolute;
    bottom: calc(100% + 4px);
    left: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
  .room {
    position: absolute;
    bottom: calc(100% + 4px);
    right: 0;
    font-size: 13px;
    color: var(--muted);
    text-shadow: 0 1px 3px black;
  }
  .commands button {
    padding: 2px 8px;
    font-size: 12px;
  }
  .list {
    display: grid;
    grid-template-columns: auto auto;
    gap: 2px 12px;
    font-size: 13px;
    white-space: nowrap;
    background: var(--panel-solid);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.6);
  }
  .list code {
    color: var(--gold);
  }
  .list span {
    color: var(--muted);
  }
  .log {
    flex: 1;
    overflow-y: auto;
    font-size: 13px;
    margin-bottom: 8px;
    text-shadow: 0 1px 2px black;
  }
  .chat form {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    white-space: nowrap;
  }
  .chat input {
    flex: 1;
    min-width: 0;
  }
  .chat input:focus {
    outline: none;
    border-color: var(--gold);
  }
  .say {
    color: #fff;
  }
  .yell {
    color: #ff4040;
  }
  .party {
    color: #aaaaff;
  }
  .system {
    color: #ffff00;
  }
  .whisper {
    color: #ff80ff;
  }
  .name {
    padding: 0;
    font: inherit;
    color: inherit;
    background: none;
    border: none;
    cursor: pointer;
  }
  .name:hover {
    text-decoration: underline;
  }
</style>
